import type { ApolloCache, Reference } from '@apollo/client'

import { getStoreFieldNameArgs } from 'modules/shift_management/utils/apolloCache/getStoreFieldNameArgs'

type ViewRange = {
  startAt: string
  endAt: string
}

type Entity = {
  __typename: string
  id: string
}

type ItemWithId = {
  id: string
}

type AppendRefsToEntityFieldParams<TItem extends ItemWithId> = {
  cache: ApolloCache<unknown>
  entity: Entity
  fieldName: string
  items: TItem[]
  refsById: Map<string, Reference>
  view: ViewRange
  shouldAppend?: (item: TItem, args: Record<string, unknown> | null) => boolean
}

const hasMatchingRange = (args: Record<string, unknown> | null, view: ViewRange) => {
  return args?.startAt === view.startAt && args?.endAt === view.endAt
}

const isReference = (value: unknown): value is Reference => {
  return typeof value === 'object' && value !== null && '__ref' in value
}

const appendRefsToEntityField = <TItem extends ItemWithId>({
  cache,
  entity,
  fieldName,
  items,
  refsById,
  view,
  shouldAppend,
}: AppendRefsToEntityFieldParams<TItem>) => {
  if (items.length === 0) return

  cache.modify({
    id: cache.identify(entity),
    fields: {
      [fieldName](existingItems = [], { readField, storeFieldName }) {
        const existingRefs = Array.isArray(existingItems) ? existingItems.filter(isReference) : []
        const args = getStoreFieldNameArgs(storeFieldName, fieldName)
        if (!hasMatchingRange(args, view)) return existingRefs

        const existingIds = new Set<string>(
          existingRefs
            .map((item: Reference) => readField('id', item))
            .filter((id: unknown): id is string => typeof id === 'string')
        )

        const refsToAdd = items.flatMap((item) => {
          if (existingIds.has(item.id)) return []
          if (shouldAppend && !shouldAppend(item, args)) return []

          const itemRef = refsById.get(item.id)
          if (!itemRef) return []

          existingIds.add(item.id)
          return [itemRef]
        })

        return refsToAdd.length > 0 ? [...existingRefs, ...refsToAdd] : existingRefs
      },
    },
  })
}

export { appendRefsToEntityField }
export type { ViewRange }
